<template>
  <div>
    <!-- In case we're showing a preview -->
    <div v-if="!isAuthenticated" class="mb-8">
      <!-- Show the group name -->
      <div class="text-h6 font-weight-bold text-center primary--text mt-3">
        {{ overview.model.name }}
      </div>

      <!-- Show the date range -->
      <div class="text-center text-subtitle-2 mt-1">
        {{ dayjs(overview.model.start_date).format("ll") }} - {{ dayjs(overview.model.end_date).format("ll") }}
      </div>
    </div>

    <!-- The header buttons -->
    <div class="d-flex justify-space-between">
      <!-- Show the go back button -->
      <v-btn
        v-if="isAuthenticated"
        text
        color="primary"
        :disabled="isMakingRequest"
        @click="$router.push({ name: 'InfluencerGroupsIndex' })"
      >
        <v-icon left> arrow_back </v-icon>

        Groups
      </v-btn>

      <!-- Don't show anything in the left -->
      <span v-else></span>

      <div class="d-flex">
        <!-- Show the menu here -->
        <v-menu v-if="isAuthenticated" :disabled="isMakingRequest" transition="slide-y-transition" offset-y bottom left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon color="primary" :disabled="isMakingRequest" v-bind="attrs" v-on="on">
              <v-icon>more_vert</v-icon>
            </v-btn>
          </template>

          <!-- Show the menu options -->
          <v-list width="160" dense>
            <!-- Show the edit button -->
            <v-list-item v-if="overview.canUserWrite" @click="$router.push({ name: 'InfluencerGroupsEdit' })">
              <v-list-item-content>
                <v-list-item-title> Edit </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- Show the option to trigger share -->
            <v-list-item v-if="overview.canUserWrite" @click="shouldShowShareDialog = true">
              <v-list-item-content>
                <v-list-item-title> Share </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- Show the option to trigger export as XLSX -->
            <v-list-item @click="triggerDownload('xlsx')" :disabled="isMakingDownloadRequest.xlsx">
              <v-list-item-content>
                <v-list-item-title> Download XLSX </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- Show the influencers table -->
    <influencers-table
      module="influencerGroups"
      :overview="overview"
      :should-show-select="true"
      :should-show-prices="true"
      :should-show-actions="true"
      :should-show-add-to-group="true"
      :should-show-add-to-campaign="true"
      :should-show-audience-overlap-button="true"
      :should-show-social-sentiments-button="true"
      class="mt-6"
      @loading="(v) => (isMakingRequest = v)"
      @refresh="(v) => $emit('refreshData')"
    >
      <template v-slot:action>
        <!-- Show the button to add influencer -->
        <v-btn
          v-if="overview.canUserWrite"
          depressed
          color="primary"
          :disabled="isMakingRequest"
          @click="$emit('triggerAddInfluencer')"
        >
          <v-icon left> add </v-icon>

          Add Influencers
        </v-btn>
      </template>
    </influencers-table>

    <!-- If we should show the invite form -->
    <template
      v-if="overview.canUserWrite && isInviteFormAllowed"
    >
      <!-- Show a divider -->
      <v-divider class="my-12"></v-divider>

      <!-- Show the container -->
      <invite-form-overview
        :overview="overview"
        module="influencerGroup"
        @refresh="handleInviteFormRefresh"
      />
    </template>

    <!-- Show a dialog for managing access -->
    <v-dialog v-model="shouldShowShareDialog" :persistent="isMakingShareRequest" max-width="680" scrollable>
      <v-card>
        <v-card-title class="primary white--text"> Share this group </v-card-title>

        <v-card-text class="pt-6">
          <!-- Show the inputs for form values -->
          <v-row>
            <!-- First show the options about team access -->
            <v-col v-if="isWhitelisted" cols="12" md="6">
              <v-select
                v-model="form.teamBehaviour"
                :items="teamBehaviourOptions"
                :label="form.teamBehaviour === 'hidden' ? 'For your teammates' : 'Let your teammates'"
                :disabled="isMakingTeamBehaviourRequest"
                :loading="isMakingTeamBehaviourRequest"
                @change="handleTeamBehaviourChange"
                color="primary"
                hide-details
                outlined
                dense
              ></v-select>
            </v-col>

            <!-- Then show the options about public access -->
            <v-col cols="12" :md="isWhitelisted ? 6 : 12">
              <v-select
                v-model="form.isShareable"
                :items="isShareableOptions"
                label="Who can see this group?"
                :disabled="isMakingShareableRequest"
                :loading="isMakingShareableRequest"
                @change="handleShareableRequest"
                color="primary"
                hide-details
                outlined
                dense
              ></v-select>
            </v-col>

            <!-- If we did allow anyone to see it -->
            <v-col v-if="form.isShareable" cols="12">
              <v-text-field
                :value="shareableURL"
                label="Click to copy shareable URL"
                @click="copyLinkToClipboard"
                color="primary"
                hide-details
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Show a divider -->
          <v-divider class="my-6" />

          <div>You can share this group with people who are not your team members.</div>

          <div class="pt-2">
            You may also invite your selected team members to grant them write access and keep your other team members on read-only
            permission.
          </div>

          <!-- Show the input for inviting new members -->
          <v-text-field
            v-model.trim="form.memberEmail"
            label="Add a new member"
            placeholder="Enter Email Address"
            color="primary"
            class="mt-4"
            append-icon="send"
            @click:append="handleInviteCreate"
            @keypress.enter="handleInviteCreate"
            :loading="isMakingCreateMemberRequest"
            :disabled="isMakingCreateMemberRequest"
            hide-details
            outlined
            dense
          ></v-text-field>

          <!-- Show current group members -->
          <div class="pt-3">
            <div v-for="item in overview.members" :key="'member-' + item.id" class="d-flex align-center mt-3">
              <!-- Show the avatar first -->
              <user-avatar :name="item.user.name" />

              <!-- Show the name and email -->
              <div class="px-3 flex-grow-1">
                <div>
                  {{ item.user.name }}
                </div>

                <div>
                  {{ item.user.email }}
                </div>
              </div>

              <!-- Show action buttons -->
              <v-menu
                :disabled="isMakingUpdateMemberRequest || isMakingDeleteMemberRequest"
                transition="slide-y-transition"
                :close-on-content-click="false"
                offset-y
                bottom
                left
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon color="primary" v-bind="attrs" v-on="on">
                    <v-icon>more_vert</v-icon>
                  </v-btn>
                </template>

                <!-- Show the menu options -->
                <v-list width="200" dense>
                  <!-- Show the update button -->
                  <v-list-item @click="handleInviteUpdate(item)">
                    <v-list-item-content>
                      <v-list-item-title>
                        Grant Write Access
                      </v-list-item-title>
                    </v-list-item-content>

                    <v-list-item-action>
                      <v-checkbox :value="item.can_write" hide-details />
                    </v-list-item-action>
                  </v-list-item>

                  <!-- Show the delete button -->
                  <v-list-item @click="handleInviteDelete(item)">
                    <v-list-item-content>
                      <v-list-item-title>
                        Remove Access
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Import helper functions
import messageEvents from "@/helpers/messageEvents"
import { getClientID } from "@/helpers/clientHelper"
import { url, email } from "vuelidate/lib/validators"

// Import children components
const InviteFormOverview = () => import(/* webpackChunkName: "crm-invite-forms-overview" */ "@/components/crm/invite-form/Overview")
const InfluencersTable = () => import(/* webpackChunkName: "crm-influencers-table" */ "@/components/crm/InfluencersTable.vue")
const PlatformSelector = () => import(/* webpackChunkName: "platform-selector" */ "@/components/crm/PlatformSelector.vue")

// Subscription ID for messageEvents
const subscriptionId = Symbol("InfluencerGroups/Influencers")

// Export the SFC
export default {
  // Name of the SFC
  name: "InfluencerGroupsInfluencers",

  // Register children components
  components: {
    InviteFormOverview,
    InfluencersTable,
    PlatformSelector,
  },

  // Accept incoming data from parent
  props: {
    overview: {
      type: Object,
      required: true
    },
  },

  // Define local data variables
  data: () => ({
    // Whether or not is the app making a network request
    isMakingRequest: false,
    // Whether or not is the app making an update request
    isMakingUpdateRequest: false,
    // Whether or not to show the download confirmation dialog
    shouldShowDownloadConfirmation: false,

    // Whether or not to show the shareable options dialog
    shouldShowShareDialog: false,
    // Whether or not is the app making a share related request
    isMakingShareRequest: false,
    // Whether or not is the teamBehaviour value changing
    isMakingTeamBehaviourRequest: false,
    // Whether or not is the isShareable value changing
    isMakingShareableRequest: false,
    // Whether or not is making a new member
    isMakingCreateMemberRequest: false,
    // Whether or not is updating a member
    isMakingUpdateMemberRequest: false,
    // Whether or not is deleting a member
    isMakingDeleteMemberRequest: false,

    // The selected influencer to whom to attach the post
    selectedInfluencer: null,
    // Whether or not to show the dialog
    shouldShowAttachPostDialog: false,
    // Whether or not is attaching a post
    isMakingAttachPostRequest: false,

    // Whether or not is making a download
    isMakingDownloadRequest: {
      pdf: false,
      xlsx: false
    },
    triggerId: {
      pdf: null,
      xlsx: null
    },
    downloadLoaders: {
      pdf: null,
      xlsx: null
    },

    form: {
      teamBehaviour: "hidden",
      isShareable: false,
      memberEmail: "",
    },

    teamBehaviourOptions: [
      {
        text: "Hide from them",
        value: "hidden"
      },
      {
        text: "See this group",
        value: "read-only"
      },
      {
        text: "Edit this group",
        value: "read-write"
      }
    ],

    isShareableOptions: [
      {
        text: "Only authorized people",
        value: false
      },
      {
        text: "Let anyone with a link to see it",
        value: true
      }
    ]
  }),

  // Computable readonly data variables
  computed: {
    /**
     * Whether or not the user is authenticated
     *
     * @returns {Boolean}
     */
    isAuthenticated() {
      return this.$store.getters["auth/isAuthenticated"]
    },

    /**
     * Get the shareable URL in string
     *
     * @returns {String}
     */
    shareableURL() {
      return `${window.location.origin}${this.$router.resolve({ name: "InfluencerGroupsView" }).href}`
    },

    /**
     * Get the logged in user's data object
     *
     * @returns {Object}
     */
    profileData() {
      return this.$store.getters["auth/profile"]
    },

    /**
     * Whether or not the currently authenticated user is whitelisted or not
     *
     * @returns {Boolean}
     */
    isWhitelisted() {
      return this.profileData ? this.profileData.is_whitelisted : false
    },

    /**
     * Whether or not to show the invite form
     *
     * @return {Boolean}
     */
    isInviteFormAllowed() {
      return this.$store.getters['auth/isServiceAllowed'](constants.model.user.allowedServices.actionInviteForm)
    },
  },

  // Define local method functions
  methods: {
    /**
     * Trigger and handle the request to change team-behaviour value
     *
     * @returns {void}
     */
    async handleTeamBehaviourChange() {
      // Set the loader
      const loaderId = Symbol()
      this.isMakingShareRequest = true
      this.isMakingTeamBehaviourRequest = true
      this.$store.dispatch("loaders/add", loaderId)

      // Try making the network request
      try {
        await axios({
          url: `/api/influencer-groups/${this.overview.model.id}/team-behaviour`,
          method: "PUT",
          data: {
            team_behaviour: this.form.teamBehaviour
          }
        })

        // If succeeded, show a message
        this.$store.dispatch("toasts/add", { text: "Influencer group updated!" })

        // Update the local store value
        this.$store.dispatch("influencerGroups/updateModel", { ...this.overview.model, team_behaviour: this.form.teamBehaviour })
      } catch (error) {
        // Catch the error
        // Log the error
        logger({ type: "InfluencerGroups/TeamBehaviour Update Error", error })

        // Show a message
        this.$store.dispatch("toasts/add", { text: error.response?.data?.message || "An error occurred, please try again!" })
      } finally {
        // Nevertheless
        // Remove the loaders
        this.isMakingShareRequest = false
        this.isMakingTeamBehaviourRequest = false
        this.$store.dispatch("loaders/remove", loaderId)
      }
    },

    /**
     * Trigger and handle the is_shareable value change
     *
     * @returns {void}
     */
    async handleShareableRequest() {
      // Set the loader
      const loaderId = Symbol()
      this.isMakingShareRequest = true
      this.isMakingShareableRequest = true
      this.$store.dispatch("loaders/add", loaderId)

      // Try making the network request
      try {
        await axios({
          url: `/api/influencer-groups/${this.overview.model.id}/is-shareable`,
          method: "PUT",
          data: {
            is_shareable: this.form.isShareable
          }
        })

        // If succeeded, show a message
        this.$store.dispatch("toasts/add", { text: "Influencer Group updated!" })

        // Update the local store value
        this.$store.dispatch("influencerGroups/updateModel", { ...this.overview.model, is_shareable: this.form.isShareable })
      } catch (error) {
        // Catch the error
        // Log the error
        logger({ type: "InfluencerGroups/IsShareable Update Error", error })

        // Show a message
        this.$store.dispatch("toasts/add", { text: error.response?.data?.message || "An error occurred, please try again!" })
      } finally {
        // Nevertheless
        // Remove the loaders
        this.isMakingShareRequest = false
        this.isMakingShareableRequest = false
        this.$store.dispatch("loaders/remove", loaderId)
      }
    },

    /**
     * Validate and make a network request for members
     *
     * @returns {void}
     */
    async handleInviteCreate() {
      // If we're already making a request
      if (this.isMakingCreateMemberRequest) {
        // Stop further execution
        return
      }

      // If the email input value isn't valid
      if (this.form.memberEmail.length === 0 || email(this.form.memberEmail) === false) {
        // Show a message
        this.$store.dispatch("toasts/add", { text: "Invalid email address" })

        return
      }

      // Otherwise, show a loader
      const loaderId = Symbol()
      this.isMakingShareRequest = true
      this.isMakingCreateMemberRequest = true
      this.$store.dispatch("loaders/add", loaderId)

      // Try making a request
      try {
        // Use helper function
        const response = await axios({
          url: `/api/influencer-groups/${this.overview.model.id}/members`,
          data: { email: this.form.memberEmail.trim().toLowerCase() },
          method: "POST"
        })

        // If we have the response, update the list
        this.$store.dispatch("influencerGroups/updateMember", { id: this.overview.model.id, member: response.data })

        // Show a message
        this.$store.dispatch("toasts/add", { text: "Added as a group member!" })

        // Reset the form
        this.form.memberEmail = ""
      } catch (error) {
        // Catch any error
        // Log the response
        logger({ type: "InfluencerGroups/CreateMember Error", error })

        // Show a message
        this.$store.dispatch("toasts/add", { text: error.response?.data?.message || "An error occurred" })
      } finally {
        // Nonetheless
        this.isMakingShareRequest = false
        this.isMakingCreateMemberRequest = false
        this.$store.dispatch("loaders/remove", loaderId)
      }
    },

    /**
     * Toggle the can write access value
     *
     * @param {Object} member
     * @returns {void}
     */
    async handleInviteUpdate(member) {
      // If we're already making a request
      if (this.isMakingUpdateMemberRequest) {
        // Stop further execution
        return
      }

      // Otherwise, show a loader
      const loaderId = Symbol()
      this.isMakingShareRequest = true
      this.isMakingUpdateMemberRequest = true
      this.$store.dispatch("loaders/add", loaderId)

      // Try making a request
      try {
        // Use helper function
        const response = await axios({
          url: `/api/influencer-groups/${this.overview.model.id}/members/${member.id}`,
          data: { can_write: !member.can_write },
          method: "PUT"
        })

        // If we have the response, update the list
        this.$store.dispatch("influencerGroups/updateMember", { id: this.overview.model.id, member: response.data })

        // Show a message
        this.$store.dispatch("toasts/add", { text: "Updated member permission!" })
      } catch (error) {
        // Catch any error
        // Log the response
        logger({ type: "influencerGroups/UpdateMember Error", error })

        // Show a message
        this.$store.dispatch("toasts/add", { text: error.response?.data?.message || "An error occurred" })
      } finally {
        // Nonetheless
        this.isMakingShareRequest = false
        this.isMakingUpdateMemberRequest = false
        this.$store.dispatch("loaders/remove", loaderId)
      }
    },

    /**
     * Remove the member from the table
     *
     * @param {Object} member
     * @returns {void}
     */
    async handleInviteDelete(member) {
      // If we're already making a request
      if (this.isMakingDeleteMemberRequest) {
        // Stop further execution
        return
      }

      // Otherwise, show a loader
      const loaderId = Symbol()
      this.isMakingShareRequest = true
      this.isMakingDeleteMemberRequest = true
      this.$store.dispatch("loaders/add", loaderId)

      // Try making a request
      try {
        // Use helper function
        await axios({
          url: `/api/influencer-groups/${this.overview.model.id}/members/${member.id}`,
          method: "DELETE"
        })

        // If we have the response, update the list
        this.$store.dispatch("influencerGroups/removeMember", { id: this.overview.model.id, member: member })

        // Show a message
        this.$store.dispatch("toasts/add", { text: "Removed member from group!" })
      } catch (error) {
        // Catch any error
        // Log the response
        logger({ type: "InfluencerGroups/RemoveMember Error", error })

        // Show a message
        this.$store.dispatch("toasts/add", { text: error.response?.data?.message || "An error occurred" })
      } finally {
        // Nonetheless
        this.isMakingShareRequest = false
        this.isMakingDeleteMemberRequest = false
        this.$store.dispatch("loaders/remove", loaderId)
      }
    },

    /**
     * Copy the shareable link to the clipboard
     *
     * @returns {void}
     */
    copyLinkToClipboard() {
      // Copy the link value
      navigator.clipboard.writeText(this.shareableURL)

      // Show a toast message
      this.$store.dispatch("toasts/add", { text: "URL copied to clipboard!" })
    },

    /**
     * Handle the request to export this group
     *
     * @param {String} type
     * @returns {void}
     */
    async triggerDownload(type) {
      // Show a global loader
      const loaderId = Symbol()
      this.$store.dispatch("loaders/add", loaderId)
      this.isMakingDownloadRequest[type] = true

      // Make a network request
      try {
        // Set a triggerId
        this.triggerId[type] = String(Date.now())

        // Use helper function
        await axios({
          url: `/api/influencer-groups/${this.overview.model.id}/download/${type}`,
          method: "POST",
          data: {
            clientId: getClientID(),
            triggerId: this.triggerId[type]
          }
        })
      } catch (error) {
        // Catch the error
        // Show a message
        this.$store.dispatch("toasts/add", { text: "An error occurred, please try later." })

        // Hide the loader
        this.isMakingDownloadRequest[type] = false
      } finally {
        // Nonetheless
        // Hide the loader
        this.$store.dispatch("loaders/remove", loaderId)

        // Show a message
        this.$store.dispatch("toasts/add", { text: "Please wait while we generate your report..." })

        // If the type is "pdf"
        if (type === "pdf") {
          // Hide the dialog
          this.shouldShowDownloadConfirmation = false
        }
      }
    },

    /**
     * Stop the download progress
     *
     * @param {Event} event
     * @returns {void}
     */
    handleMessageEvent(event) {
      // If the clientId does not match
      if (getClientID() !== event.localData.clientId) {
        // Stop further execution
        return
      }

      // Switch through the cases
      switch (event.key) {
        case "generate-influencer-groups-xlsx-started":
          // Check if the triggerId does not match
          if (this.triggerId.xlsx !== event.localData.triggerId) {
            // Stop further execution
            return
          }

          // Show a global loader
          this.downloadLoaders.xlsx = Symbol()
          this.$store.dispatch("loaders/add", this.downloadLoaders.xlsx)

          break

        case "generate-influencer-groups-xlsx-completed":
          // Check if the triggerId does not match
          if (this.triggerId.xlsx !== event.localData.triggerId) {
            // Stop further execution
            return
          }

          // Show a message saying it succeeded
          this.$store.dispatch("toasts/add", { text: "Downloading your report..." })

          // Hide progress
          this.$store.dispatch("loaders/remove", this.downloadLoaders.xlsx)
          this.isMakingDownloadRequest.xlsx = false

          break

        case "generate-influencer-groups-xlsx-failed":
          // Check if the triggerId does not match
          if (this.triggerId.xlsx !== event.localData.triggerId) {
            // Stop further execution
            return
          }

          // Show a message saying it succeeded
          this.$store.dispatch("toasts/add", { text: "An error occurred, please contact us." })

          // Hide progress
          this.$store.dispatch("loaders/remove", this.downloadLoaders.xlsx)
          this.isMakingDownloadRequest.xlsx = false

          break
      }
    },

    /**
     * Handle the invite form refresh call
     *
     * @param {Object} inviteForm
     * @returns {void}
     */
    handleInviteFormRefresh(inviteForm) {
      // Call the event to refresh the table
      window.dispatchEvent(new CustomEvent("refreshInfluencersTable", {
        detail: {
          module: "influencerGroups",
          id: this.overview.model.id
        }
      }))

      // Update the unapproved influencers count
      this.$store.dispatch("influencerGroups/updateInviteForm", inviteForm)
    }
  },

  /**
   * As soon as the component data is ready
   *
   * @returns {void}
   */
  created() {
    // Set the default form values
    this.form.teamBehaviour = this.overview.model.team_behaviour
    this.form.isShareable = this.overview.model.is_shareable

    // Register a subscriber for messageEvents
    messageEvents.register({
      id: subscriptionId,
      module: "influencer-groups",
      type: "all",
      key: "all",
      validator: (event) => event.module === "influencer-groups" && event.localData.modelId === this.overview.model.id,
      callback: this.handleMessageEvent
    })
  },

  /**
   * Before this component is about to be removed
   *
   * @returns {void}
   */
  beforeDestroy() {
    // De-register the event listener
    messageEvents.deregister(subscriptionId)
  }
}
</script>

<style lang="stylus" scoped>
.contain-select-width
  max-width 10em
</style>
